import {get} from "@vueuse/core";
import type {Icon} from "../types";

import type {MaybeRef} from "vue";
import {computed} from "vue";
import {useFetch} from "nuxt/app";

export function useReviews() {
	const {csrf} = useCsrf();

	const getRating = (skus: string[]) => {
		const {data, status} = useFetch("/api/reviews/rating", {
			query: {
				skus: skus.join(","),
			},
			dedupe: "defer",
			headers: {
				...(csrf && {
					"csrf-token": csrf,
				}),
			},
		});

		const rating = computed(() =>
			data.value?.averageRating ? Number(data.value.averageRating) : 0,
		);

		const totalRatingCount = computed(() => data.value?.totalRatingCount || 0);

		return {
			rating,
			totalRatingCount,
			status,
		};
	};

	const getReviews = (
		skus: string[],
		page: MaybeRef<number>,
		extraKey?: string,
	) => {
		const {data, status} = useFetch("/api/reviews/reviews", {
			query: {
				skus: skus.join(","),
				page,
				// We append an unused dummy query parameter into the request.
				// Nuxt computes the request's key automatically from its
				// parameters (and providing a reactive `key` param is not
				// supported), so this makes it possible to have the same query
				// in different components but without sharing the response
				// data between them.
				// https://github.com/nuxt/nuxt/issues/21532

				...(extraKey && {_dummy: extraKey}),
			},
			headers: {
				...(csrf && {
					"csrf-token": csrf,
				}),
			},
		});

		const reviews = computed(() => data.value?.items || []);

		const pageCount = computed(() => data.value?.pageCount || 0);

		const totalCount = computed(() => data.value?.totalCount || 0);

		const totalRatings = computed(() => data.value?.totalRatings || []);

		return {
			reviews,
			pageCount,
			totalCount,
			status,
			totalRatings,
		};
	};

	/**
	 * Fetches simplified list of ratings to be used inside schema (json+ld)
	 */
	const getSchemaReviews = (skus: string[]) => {
		const {data} = useFetch("/api/reviews/schema-reviews", {
			query: {
				skus: skus.join(","),
			},
			dedupe: "defer",
			headers: {
				...(csrf && {
					"csrf-token": csrf,
				}),
			},
		});

		const schemaReviews = computed(() => data.value?.items || []);

		return {
			schemaReviews,
		};
	};

	/** This fetches all the review images (along with their reviews) for a product (SKU), no pagination */
	const getReviewsWithImages = (skus: string[], extraKey?: string) => {
		const {data} = useFetch("/api/reviews/images", {
			query: {
				skus: skus.join(","),
				// We append an unused dummy query parameter into the request.
				// Nuxt computes the request's key automatically from its
				// parameters (and providing a reactive `key` param is not
				// supported), so this makes it possible to have the same query
				// in different components but without sharing the response
				// data between them.
				// https://github.com/nuxt/nuxt/issues/21532

				...(extraKey && {_dummy: extraKey}),
			},
			headers: {
				...(csrf && {
					"csrf-token": csrf,
				}),
			},
		});

		const images = computed(() => data.value || []);

		return {
			images,
		};
	};

	const getIcon = (index: number, rating: MaybeRef<number>): Icon => {
		if (get(rating) >= index) {
			return "star";
		}

		if (get(rating) >= index - 0.5) {
			return "star-half";
		}

		return "star-faded";
	};

	// The images from 'https://developer.reviews.io/reference/899f58c2c4431ec1000ec59a38b2916b'
	// seem be routed through some internal media proxy service.
	// We can adjust just size by query params
	// Eg. https://media.reviews.co.uk/resize/create?width=600&height=0&src=...
	const getRezisedImageUrl = (urlString: string, size: number) => {
		const url = new URL(urlString);
		url.searchParams.set("width", size.toString());
		url.searchParams.set("height", size.toString());
		return url.href;
	};

	return {
		getRating,
		getIcon,
		getReviews,
		getReviewsWithImages,
		getSchemaReviews,
		getRezisedImageUrl,
	};
}
